import { communication, requestWhatsappConfirmation, type Connection } from '@/lib/communication';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, LogOut, RotateCw } from 'lucide-react';
import { api } from '@/lib/api';
import { getPlanStatus, type PlanStatus } from '@shared/pricing';
import { identify, trackOnce } from '@/lib/analytics';
import { errorMessage } from '@/lib/utils';
import type { AuthUser, Employee, Org } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { FullScreenLoader } from '@/components/brand';
import { useAuth } from './auth';
import { qk, queryClient } from './query';

interface WorkspaceValue {
  user: AuthUser;
  org: Org;
  orgId: string;
  plan: PlanStatus;
  refreshOrg: () => Promise<unknown>;
}

const WorkspaceContext = createContext<WorkspaceValue | null>(null);

export function WorkspaceProvider({ user, children }: { user: AuthUser; children: ReactNode }) {
  const { signOut } = useAuth();
  const orgQuery = useQuery({
    queryKey: qk.org(user.id),
    queryFn: () => api.org.resolveOrg(user),
    staleTime: 60_000,
    retry: 1,
  });

  const org = orgQuery.data;

  useEffect(() => {
    if (org) {
      identify(user, { id: org.id, name: org.name, planId: org.planId });
      trackOnce(`workspace-ready:${org.id}:${user.id}`, 'employer_workspace_ready');
      // Funnel step only. Meta StartTrial is the workspace's first synced call (see ANALYTICS.md).
      const age = org.createdAt ? Date.now() - new Date(org.createdAt).getTime() : Infinity;
      if (org.planId === 'trial' && org.creatorId === user.id && age < 48 * 3_600_000)
        trackOnce(`trial:${org.id}`, 'trial_created');
    }
  }, [org, user]);

  useEffect(() => {
    if (!org || user.metadata.whatsapp_notifications !== true || !/^\+[1-9]\d{6,14}$/.test(user.phone)) return;
    const key = `whatsapp-confirmation/${user.id}/${user.phone}`;
    if (sessionStorage.getItem(key)) return;
    let active = true;
    communication<Connection>('/account/notifications').then(async (connection) => {
      if (!active || connection.linked || !connection.automatic_messages_enabled) return;
      await requestWhatsappConfirmation(user.phone, 'automatic_signup');
      sessionStorage.setItem(key, 'requested');
    }).catch(() => { /* Account notifications exposes retry and provider errors. */ });
    return () => { active = false; };
  }, [org?.id, user.id, user.phone, user.metadata.whatsapp_notifications]);

  const value = useMemo<WorkspaceValue | null>(() => {
    if (!org) return null;
    return {
      user,
      org,
      orgId: org.id,
      plan: getPlanStatus(org),
      refreshOrg: () => queryClient.invalidateQueries({ queryKey: qk.org(user.id) }),
    };
  }, [org, user]);

  if (orgQuery.isPending) return <FullScreenLoader label="Loading your workspace" />;

  if (!value) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm rounded-2xl border border-border bg-surface p-6 text-center shadow-sm">
          <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-full bg-danger-soft text-danger">
            <AlertTriangle className="size-5" />
          </div>
          <h1 className="text-base font-semibold">We couldn’t open your workspace</h1>
          <p className="mt-1 text-sm text-fg-muted">{errorMessage(orgQuery.error)}</p>
          <div className="mt-5 flex justify-center gap-2">
            <Button variant="secondary" onClick={() => signOut()}>
              <LogOut className="size-4" /> Sign out
            </Button>
            <Button onClick={() => orgQuery.refetch()} loading={orgQuery.isFetching}>
              <RotateCw className="size-4" /> Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used inside WorkspaceProvider');
  return ctx;
}

// Consumers share cached employees; periodic refresh is explicitly enabled only during setup.
export function useEmployees(poll = false) {
  const { orgId } = useWorkspace();
  return useQuery<Employee[]>({
    queryKey: qk.employees(orgId),
    queryFn: () => api.employees.listEmployees(orgId),
    staleTime: 60_000,
    refetchInterval: poll ? 60_000 : false,
  });
}

export function useLabels() {
  const { orgId } = useWorkspace();
  return useQuery<string[]>({
    queryKey: qk.labels(orgId),
    queryFn: () => api.calls.listLabels(orgId),
    staleTime: 5 * 60_000,
  });
}
